// src/modules/meridian/components/documentType/MeridianStructureAgent/FloatingChatWidget.tsx
//
// Floating chat widget that expands to show the Structure Agent chat panel.
// On first visit the button morphs into a pill ("✨ AI Assistant") for 3s to
// signal the feature, then contracts back to the icon. The behaviour is gated
// behind a localStorage flag so it only fires once per browser.

import React, { useState, useEffect } from "react";
import { Box, Button, Sheet, Tooltip } from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ChatPanel } from "./ChatPanel";
import type { DocumentTypeSection } from "../../../types/documentTypes";
import type { DocumentTypeInput } from "../../../types/documentTypes";

const STRUCTURE_AGENT_SEEN_KEY = "meridian_structure_agent_seen";

interface FloatingChatWidgetProps {
  documentTypeId: number;
  documentTypeName: string;
  documentTypeDescription?: string;
  sections: DocumentTypeSection[];
  dataFields: DocumentTypeInput[];
  workspaceId: string;
  onSectionsChanged: () => void;
}

/**
 * Floating chat widget that appears in the lower-right corner.
 * Clicking the button expands/collapses the chat panel.
 */
export const FloatingChatWidget: React.FC<FloatingChatWidgetProps> = ({
  documentTypeId,
  documentTypeName,
  workspaceId,
  onSectionsChanged,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPillExpanded, setIsPillExpanded] = useState(false);

  // First-visit pill animation: expand for 3 seconds then contract.
  useEffect(() => {
    if (localStorage.getItem(STRUCTURE_AGENT_SEEN_KEY)) return;
    setIsPillExpanded(true);
    const timer = setTimeout(() => {
      setIsPillExpanded(false);
      localStorage.setItem(STRUCTURE_AGENT_SEEN_KEY, "1");
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const toggleChat = () => {
    // Clicking while pill is expanded dismisses it immediately.
    if (isPillExpanded) {
      setIsPillExpanded(false);
      localStorage.setItem(STRUCTURE_AGENT_SEEN_KEY, "1");
    }
    setIsOpen((prev) => !prev);
  };

  // Show the pill text only when not yet opened by the user.
  const showPillText = isPillExpanded && !isOpen;

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <Sheet
          variant="outlined"
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            width: 520,
            height: "min(700px, calc(100vh - 160px))",
            minHeight: 400,
            borderRadius: "lg",
            boxShadow: "lg",
            zIndex: 1300,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ChatPanel
            documentTypeId={documentTypeId}
            documentTypeName={documentTypeName}
            workspaceId={workspaceId}
            onSectionsChanged={onSectionsChanged}
            onClose={toggleChat}
          />
        </Sheet>
      )}

      {/* Floating Button — morphs between circle and pill */}
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1300,
        }}
      >
        <Tooltip
          title="AI Structure Assistant"
          placement="left"
          arrow
          disableHoverListener={showPillText}
        >
          <Button
            onClick={toggleChat}
            variant="solid"
            color={isOpen ? "neutral" : "primary"}
            sx={{
              height: 48,
              minWidth: 48,
              // borderRadius stays at half the height so the ends are always round;
              // the pill effect comes from the width growing to fit the label.
              borderRadius: "24px",
              // No horizontal padding on the button itself — the inner Box handles it
              // so we get precise control over the icon centering.
              px: 0,
              boxShadow: "md",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "lg",
              },
            }}
          >
            {/* Icon — always visible, horizontally padded to centre in circle mode */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                pl: 1.5,
                pr: showPillText ? 0.5 : 1.5,
                transition: "padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {isOpen ? <CloseIcon /> : <AutoAwesomeIcon />}
            </Box>

            {/* Label — slides in/out via max-width transition */}
            <Box
              sx={{
                maxWidth: showPillText ? "140px" : 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                pr: showPillText ? 1.5 : 0,
                transition:
                  "max-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding-right 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              AI Assistant
            </Box>
          </Button>
        </Tooltip>
      </Box>
    </>
  );
};

export default FloatingChatWidget;
